import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";

type TestType = {
  id?: number | string;
  name?: string;
  description?: string;
};

type TestTypeForm = {
  name: string;
  description: string;
};

type TestTypeEditModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  testType?: TestType;
  onSave?: (testType: unknown) => void;
};

export default function TestTypeEditModal({ isOpen, onClose, testType, onSave }: TestTypeEditModalProps) {
  const [form, setForm] = useState<TestTypeForm>({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (testType) {
      setForm({
        name: testType.name || "",
        description: testType.description || ""
      });
    }
  }, [testType]);

  function handleChange(field: keyof TestTypeForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const resp = await RightTimeApi.putAsync(`/TestType/${testType.id}`, form);
      if (resp && resp.ok) {
        toast.success("Test type updated");
        if (onSave) onSave(resp.jsonData);
        if (onClose) onClose();
      } else {
        toast.error("Failed to update test type");
      }
    } catch {
      toast.error("Error updating test type");
    } finally {
      setSaving(false);
    }
  }

  const fields: Record<string, ReactNode> = {
    "Name *": (
      <input
        type="text"
        value={form.name}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("name", e.target.value)}
        required
      />
    ),
    "Description": (
      <input
        type="text"
        value={form.description}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("description", e.target.value)}
      />
    )
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Test Type (${testType?.id || "New"})`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <ModalFormFields fields={fields} />
    </Modal>
  );
}


